// Import external scripts
import template from '../external/template.js';

// Export the scripts for use in components
export const externalScripts = {
  template
};

// Function to verify script exists
const verifyScriptExists = async (scriptPath) => {
  try {
    const response = await fetch(scriptPath, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    //console.error(`Error verifying script: ${scriptPath}`, error);
    return false;
  }
};

// Function to load external scripts dynamically
export const loadExternalScript = async (scriptPath) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Verify script exists
      const exists = await verifyScriptExists(scriptPath);
      if (!exists) {
        reject(new Error(`Script not found: ${scriptPath}`));
        return;
      }

      // Check if script is already loaded
      const existingScript = document.querySelector(`script[src="${scriptPath}"]`);
      if (existingScript) {
        //console.log(`Script already loaded: ${scriptPath}`);
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = scriptPath;
      script.async = false;
      script.defer = true;
      script.type = 'text/javascript';
      
      script.onload = () => {
       // console.log(`Loaded script: ${scriptPath}`);
        resolve();
      };
      
      script.onerror = (error) => {
        //console.error(`Error loading script: ${scriptPath}`, error);
        reject(error);
      };

      document.head.appendChild(script);
    } catch (error) {
      //console.error(`Error in loadExternalScript for ${scriptPath}:`, error);
      reject(error);
    }
  });
};

// Function to check if jQuery is loaded
const isJQueryLoaded = () => {
  return typeof window.jQuery !== 'undefined' && typeof window.$ !== 'undefined';
};

// Function to load all scripts in order
export const loadAllScripts = async () => {
  const scripts = [
    {
      src: 'https://code.jquery.com/jquery-3.6.0.min.js',
      id: 'jquery',
      async: false,
      defer: true
    }
  ];

  // Only load invoice bundle if we're on a page that needs it
  if (window.location.pathname.includes('/invoices') || window.location.pathname.includes('/billing')) {
    scripts.push({
      src: '/assets/bundles/invoice.bundle.js',
      id: 'invoice-bundle',
      async: false,
      defer: true,
      onLoad: () => {
        try {
          // Initialize invoice bundle if it exists
          if (typeof window.invoiceBundle !== 'undefined') {
            // Store the original init function
            const originalInit = window.invoiceBundle.init;
            
            // Override the init function to prevent execution on visibility change
            window.invoiceBundle.init = function() {
              // Only execute if the page is visible
              if (document.visibilityState === 'visible') {
                originalInit.call(this);
              }
            };

            // Initialize only if the page is visible
            if (document.visibilityState === 'visible') {
              window.invoiceBundle.init();
            }
          }
        } catch (error) {
          //console.error('Error initializing invoice bundle:', error);
        }
      }
    });
  }

  const loadScript = (script) => {
    return new Promise((resolve, reject) => {
      try {
        // Check if script is already loaded
        if (document.getElementById(script.id)) {
          //console.log(`Script ${script.id} already loaded`);
          resolve();
          return;
        }

        const scriptElement = document.createElement('script');
        scriptElement.src = script.src;
        scriptElement.id = script.id;
        scriptElement.async = script.async;
        scriptElement.defer = script.defer;
        scriptElement.setAttribute('data-dynamic', 'true'); // Mark as dynamically added

        // Add error handling
        scriptElement.onerror = (error) => {
          //console.error(`Error loading script ${script.id}:`, error);
          // Don't reject for invoice bundle to prevent app crash
          if (script.id === 'invoice-bundle') {
            //console.warn('Invoice bundle failed to load, continuing without it');
            resolve();
          } else {
            reject(error);
          }
        };

        // Add load handling
        scriptElement.onload = () => {
          //console.log(`Script ${script.id} loaded successfully`);
          // For jQuery, verify it's actually loaded
          if (script.id === 'jquery') {
            if (isJQueryLoaded()) {
              resolve();
            } else {
              reject(new Error('jQuery failed to load properly'));
            }
          } else {
            // Call onLoad handler if provided
            if (script.onLoad) {
              script.onLoad();
            }
            resolve();
          }
        };

        // Add to document
        document.body.appendChild(scriptElement);
      } catch (error) {
       // console.error(`Error creating script element for ${script.id}:`, error);
        // Don't reject for invoice bundle to prevent app crash
        if (script.id === 'invoice-bundle') {
         // console.warn('Invoice bundle failed to load, continuing without it');
          resolve();
        } else {
          reject(error);
        }
      }
    });
  };

  try {
    // Load scripts sequentially
    for (const script of scripts) {
      await loadScript(script);
    }
    //console.log('All scripts loaded successfully');
  } catch (error) {
    //console.error('Error loading scripts:', error);
    // Don't throw the error, just log it
  }
};

// Add visibility change handler
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && typeof window.invoiceBundle !== 'undefined') {
    try {
      window.invoiceBundle.init();
    } catch (error) {
      //console.error('Error reinitializing invoice bundle on visibility change:', error);
    }
  }
}); 